/**
 * BrainyTots Assessment Results Page
 * Fetches and displays comprehensive assessment results with trust-building elements
 */

class BrainyTotsResults {
  constructor() {
    this.apiBase = this.getAPIBase();
    this.childName = '';
    this.init();
  }

  getAPIBase() {
    // Check for override (useful for development/testing)
    if (typeof window !== 'undefined' && window.__BT_API_BASE__) {
      return window.__BT_API_BASE__;
    }
    // Default to production backend
    return 'https://bt-development.skillit.in';
  }

  init() {
    // Get session ID from URL
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (!sessionId) {
      console.error('No session ID found in URL');
      this.showError('Invalid session. Please start a new assessment.');
      return;
    }

    // Load results
    this.loadResults(sessionId);
  }

  async loadResults(sessionId) {
    try {
      this.showLoadingState();
      
      // Fetch results from comprehensive endpoint
      const response = await fetch(`${this.apiBase}/results?session_id=${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load results: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      this.childName = data.child_name || '';
      this.hideLoadingState();
      this.displayResults(data);
      
    } catch (error) {
      console.error('Error loading results:', error);
      this.hideLoadingState();
      this.showError(`Failed to load results: ${error.message}`);
    }
  }

  showLoadingState() {
    const containers = [
      'bt-methodology-container',
      'bt-domain-scores-container',
      'bt-activities-container',
      'bt-products-container',
    ];
    containers.forEach(id => {
      const container = document.getElementById(id);
      if (container) {
        container.innerHTML = `
          <div style="text-align: center; padding: 40px;">
            <div style="
              display: inline-block;
              width: 40px;
              height: 40px;
              border: 4px solid #e0e0e0;
              border-top: 4px solid var(--bt-sage-green);
              border-radius: 50%;
              animation: spin 1s linear infinite;
            "></div>
            <p style="margin-top: 16px; color: #666;">Loading your results...</p>
          </div>
          <style>
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          </style>
        `;
      }
    });
  }

  hideLoadingState() {
    // Loading states will be replaced by actual content
  }

  showError(message) {
    const mainContainer = document.querySelector('.bt-assessment-results');
    if (mainContainer) {
      mainContainer.innerHTML = `
        <div style="
          background: #f8d7da;
          color: #721c24;
          padding: 20px;
          border-radius: 8px;
          margin: 20px;
          text-align: center;
        ">
          <h3 style="margin: 0 0 10px 0;">⚠️ Error Loading Results</h3>
          <p style="margin: 0;">${message}</p>
          <button 
            onclick="window.location.href='/?restart=true'"
            style="
              margin-top: 16px;
              background: #721c24;
              color: white;
              border: none;
              padding: 10px 20px;
              border-radius: 6px;
              cursor: pointer;
            "
          >
            Start New Assessment
          </button>
        </div>
      `;
    }
  }

  displayResults(data) {
    // Update child name in header
    this.updateChildNameInHeader(data.child_name, data.age_months);
    
    // Display methodology
    this.displayMethodology(data);
    
    // Display domain scores with answer examples
    this.displayDomainScores(data.domain_scores, data.overall_status, data.answer_examples);
    
    // Display activities (show before products for immediate content)
    this.displayActivities(data.activities);
    
    // Display recommendation bridge
    this.displayRecommendationBridge(data);
    
    // Display recommendations with Shopify links (background loading starts here)
    this.displayRecommendations(data.recommendations, data.recommendations_summary, data.child_name);
    
    // Bind event handlers
    this.bindEvents();
    
    // Trigger animations
    this.triggerScrollAnimations();
  }

  updateChildNameInHeader(childName, ageMonths) {
    // Update ONLY the dedicated container (new implementation)
    const container = document.getElementById('bt-child-name-age-container');
    if (container) {
      const ageYears = Math.floor(ageMonths / 12);
      const remainingMonths = Math.round(ageMonths % 12);
      let ageText = '';
      
      if (ageYears > 0) {
        ageText = `${ageYears} year${ageYears > 1 ? 's' : ''}`;
        if (remainingMonths > 0) {
          ageText += ` ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
        }
      } else {
        ageText = `${Math.round(ageMonths)} month${Math.round(ageMonths) > 1 ? 's' : ''}`;
      }
      
      // Create highlighted child info display
      container.innerHTML = `
        <div class="bt-child-name-age-highlight" style="
          background: rgba(255, 255, 255, 0.15);
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          padding: 16px 24px;
          backdrop-filter: blur(4px);
          display: inline-block;
          margin: 0;
        ">
          <div style="
            font-size: 28px;
            font-weight: 700;
            color: white;
            margin: 0;
            line-height: 1.2;
            font-family: 'Quicksand', sans-serif;
          ">
            <span style="background: linear-gradient(135deg, #FFE5E5 0%, #FFFAFF 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;">
              ${childName}
            </span>
          </div>
          <div style="
            font-size: 16px;
            font-weight: 500;
            color: rgba(255, 255, 255, 0.85);
            margin-top: 6px;
            letter-spacing: 0.5px;
          ">
            👶 Age: ${ageText}
          </div>
        </div>
      `;
      return; // Exit early - don't process fallback logic
    }
    
    // Fallback: If dedicated container doesn't exist, update hero container (backward compatibility)
    const heroContainer = document.querySelector('.bt-results-hero');
    if (heroContainer) {
      const ageYears = Math.floor(ageMonths / 12);
      const remainingMonths = Math.round(ageMonths % 12);
      let ageText = '';
      
      if (ageYears > 0) {
        ageText = `${ageYears} year${ageYears > 1 ? 's' : ''}`;
        if (remainingMonths > 0) {
          ageText += ` ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
        }
      } else {
        ageText = `${Math.round(ageMonths)} month${Math.round(ageMonths) > 1 ? 's' : ''}`;
      }
      
      // Only insert if not already present
      if (!heroContainer.querySelector('.bt-child-info')) {
        const nameDisplay = document.createElement('div');
        nameDisplay.className = 'bt-child-info';
        nameDisplay.innerHTML = `
          <p style="font-size: 20px; font-weight: 600; margin: 8px 0; opacity: 0.95;">
            ${childName} • ${ageText}
          </p>
        `;
        const titleElement = heroContainer.querySelector('h1');
        if (titleElement && titleElement.parentNode) {
          titleElement.parentNode.insertBefore(nameDisplay, titleElement.nextSibling);
        }
      }
    }
  }

  displayMethodology(data) {
    const container = document.getElementById('bt-methodology-container');
    if (!container) return;

    const sourcesText = data.milestone_sources && data.milestone_sources.length > 0 
      ? data.milestone_sources.join(', ') 
      : 'CDC, WHO, AAP';

    container.innerHTML = `
      <div class="bt-methodology-panel">
        <button class="bt-methodology-toggle" id="bt-methodology-toggle">
          <span class="bt-toggle-icon">+</span>
          <span class="bt-toggle-text">How We Assessed ${data.child_name}'s Development</span>
        </button>
        <div class="bt-methodology-content" id="bt-methodology-content" style="display: none;">
          <div class="bt-trust-grid">
            <div class="bt-trust-item">
              <div class="bt-trust-icon">📋</div>
              <h4>Comprehensive Assessment</h4>
              <p>${data.methodology_summary || `We analyzed ${data.total_questions} age-appropriate questions across 5 developmental domains.`}</p>
            </div>
            <div class="bt-trust-item">
              <div class="bt-trust-icon">🏥</div>
              <h4>Science-Backed</h4>
              <p>Our assessment is based on established milestones from ${sourcesText}.</p>
            </div>
            <div class="bt-trust-item">
              <div class="bt-trust-icon">🎯</div>
              <h4>Personalized Results</h4>
              <p>Recommendations are tailored to ${data.child_name}'s specific developmental profile.</p>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  displayDomainScores(scores, overallStatus, answerExamples = []) {
    const container = document.getElementById('bt-domain-scores-container');
    if (!container) return;

    const labelColors = {
      'ahead_of_curve': '#27AE60',
      'on_track': '#3498DB',
      'watch_closely': '#F39C12',
      'practice_more': '#E74C3C',
    };

    const labelText = {
      'ahead_of_curve': '🌟 Ahead of Curve',
      'on_track': '✅ On Track',
      'watch_closely': '👀 Watch Closely',
      'practice_more': '💪 Practice More',
    };
    
    const domainIcons = {
      'Gross Motor': '🏃',
      'Fine Motor': '✋',
      'Language & Communication': '💬',
      'Social-Emotional': '🤗',
      'Cognitive/Problem-Solving': '🧩',
    };

    // Group answer examples by domain
    const examplesByDomain = {};
    answerExamples.forEach(example => {
      if (!examplesByDomain[example.domain]) {
        examplesByDomain[example.domain] = [];
      }
      examplesByDomain[example.domain].push(example);
    });

    // Build accordion-style cards (summary + collapsible details)
    const accordionHTML = scores.map((score, index) => {
      const examples = examplesByDomain[score.domain] || [];
      const isFirst = index === 0;
      
      // Gradient colors based on status
      const gradients = {
        'ahead_of_curve': 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
        'on_track': 'linear-gradient(135deg, #3498DB 0%, #5DADE2 100%)',
        'watch_closely': 'linear-gradient(135deg, #F39C12 0%, #F8B500 100%)',
        'practice_more': 'linear-gradient(135deg, #E74C3C 0%, #EC7063 100%)',
      };
      
      const shadowColors = {
        'ahead_of_curve': 'rgba(39, 174, 96, 0.3)',
        'on_track': 'rgba(52, 152, 219, 0.3)',
        'watch_closely': 'rgba(243, 156, 18, 0.3)',
        'practice_more': 'rgba(231, 76, 60, 0.3)',
      };
      
      // Answer examples HTML
      const examplesHTML = examples.length > 0 ? `
        <div class="bt-answer-examples" id="bt-examples-${index}" style="display: none; margin-top: 16px;">
          <h5 style="margin: 12px 0 8px 0; font-size: 14px; color: white; opacity: 0.9;">Sample Questions:</h5>
          ${examples.map(ex => `
            <div class="bt-answer-example" style="background: rgba(255,255,255,0.15); backdrop-filter: blur(10px); padding: 12px; border-radius: 12px; margin-bottom: 8px; border: 1px solid rgba(255,255,255,0.2);">
              <p class="bt-example-question" style="margin: 0 0 4px 0; font-size: 13px; color: white;"><strong>Q:</strong> ${ex.question_text}</p>
              <p class="bt-example-answer" style="margin: 0; font-size: 13px; color: rgba(255,255,255,0.9);"><strong>Response:</strong> ${ex.user_answer}</p>
              ${ex.milestone_source ? `<span class="bt-milestone-source" style="font-size: 11px; color: rgba(255,255,255,0.7); margin-top: 4px; display: inline-block;">Source: ${ex.milestone_source}</span>` : ''}
            </div>
          `).join('')}
        </div>
      ` : '';

      // Domain details section
      const domainDetailsHTML = score.domain_explanation || score.expected_activities?.length > 0 ? `
        <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,0.2);">
          ${score.domain_explanation ? `
            <div style="margin-bottom: 12px;">
              <h5 style="margin: 0 0 6px 0; font-size: 14px; font-weight: 600; color: white;">💡 What this means at this age:</h5>
              <p style="margin: 0; font-size: 13px; color: rgba(255,255,255,0.9); line-height: 1.6;">${score.domain_explanation}</p>
            </div>
          ` : ''}
          ${score.expected_activities && score.expected_activities.length > 0 ? `
            <div style="margin-bottom: 12px;">
              <h5 style="margin: 0 0 6px 0; font-size: 14px; font-weight: 600; color: white;">👀 What to look for:</h5>
              <ul style="margin: 0; padding-left: 20px; font-size: 13px; color: rgba(255,255,255,0.9);">
                ${score.expected_activities.map(activity => `<li style="margin-bottom: 4px;">${activity}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
          ${score.assessment_observations ? `
            <div>
              <h5 style="margin: 0 0 6px 0; font-size: 14px; font-weight: 600; color: white;">📝 Our observation:</h5>
              <p style="margin: 0; font-size: 13px; color: rgba(255,255,255,0.9); line-height: 1.6;">${score.assessment_observations}</p>
            </div>
          ` : ''}
        </div>
      ` : '';

      return `
        <div class="bt-accordion-item" data-domain-index="${index}" style="margin-bottom: 20px;">
          <!-- Summary Card (Always Visible) -->
          <div 
            class="bt-accordion-header ${isFirst ? 'expanded' : ''}" 
            role="button"
            tabindex="0"
            aria-expanded="${isFirst}"
            aria-controls="bt-accordion-content-${index}"
            data-gradient="${gradients[score.label] || gradients['on_track']}"
            data-shadow="${shadowColors[score.label] || shadowColors['on_track']}"
            data-border-color="${labelColors[score.label] || '#999'}"
            style="
              background: ${isFirst ? gradients[score.label] || gradients['on_track'] : 'white'};
              border: 2px solid ${isFirst ? 'transparent' : (labelColors[score.label] || '#999')};
              border-radius: ${isFirst ? '24px 24px 0 0' : '16px'};
              padding: ${isFirst ? '20px 24px' : '16px 20px'};
              cursor: pointer;
              transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
              position: relative;
              box-shadow: ${isFirst ? '0 8px 24px ' + (shadowColors[score.label] || shadowColors['on_track']) : '0 2px 8px rgba(0,0,0,0.08)'};
              overflow: ${isFirst ? 'hidden' : 'visible'};
            "
          >
            ${isFirst ? `
              <!-- Decorative circles (only when expanded) -->
              <div style="position: absolute; top: -30px; right: -30px; width: 120px; height: 120px; background: rgba(255,255,255,0.1); border-radius: 50%; pointer-events: none;"></div>
              <div style="position: absolute; bottom: -20px; left: -20px; width: 80px; height: 80px; background: rgba(255,255,255,0.1); border-radius: 50%; pointer-events: none;"></div>
            ` : ''}
            
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; position: relative; z-index: 1;">
              <div style="display: flex; align-items: center; gap: 12px; flex: 1;">
                <span class="bt-domain-emoji" style="font-size: ${isFirst ? '40px' : '32px'}; filter: ${isFirst ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'}; transition: all 0.3s ease;">${domainIcons[score.domain] || '📊'}</span>
                <div style="flex: 1;">
                  <h4 style="margin: 0; color: ${isFirst ? 'white' : 'var(--bt-charcoal)'}; font-size: 18px; font-weight: ${isFirst ? '700' : '600'}; text-shadow: ${isFirst ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'}; transition: color 0.4s ease;">${score.domain}</h4>
                  <div style="font-size: 11px; color: ${isFirst ? 'rgba(255,255,255,0.85)' : '#999'}; margin-top: 3px; font-weight: 500;">Tap to ${isFirst ? 'collapse' : 'expand'}</div>
                </div>
              </div>
              <div style="display: flex; align-items: center; gap: 12px;">
                <span style="
                  background: ${isFirst ? 'rgba(255,255,255,0.25)' : labelColors[score.label] || '#999'};
                  backdrop-filter: ${isFirst ? 'blur(10px)' : 'none'};
                  color: ${isFirst ? 'white' : 'white'};
                  padding: 6px 12px;
                  border-radius: 16px;
                  font-size: 11px;
                  font-weight: 600;
                  white-space: nowrap;
                  text-shadow: ${isFirst ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'};
                  border: ${isFirst ? '1px solid rgba(255,255,255,0.2)' : 'none'};
                  transition: all 0.4s ease;
                ">
                  ${labelText[score.label] || score.label}
                </span>
                <span class="bt-accordion-icon" style="
                  font-size: 20px; 
                  color: ${isFirst ? 'white' : (labelColors[score.label] || '#999')}; 
                  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); 
                  display: inline-block; 
                  filter: ${isFirst ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none'};
                  transform: ${isFirst ? 'rotate(180deg)' : 'rotate(0deg)'};
                ">
                  ▼
                </span>
              </div>
            </div>
            
            <!-- Progress Bar (Always Visible) -->
            <div style="background: ${isFirst ? 'rgba(255,255,255,0.25)' : '#f5f5f5'}; border-radius: ${isFirst ? '12px' : '8px'}; overflow: hidden; height: ${isFirst ? '10px' : '8px'}; margin-bottom: 8px; position: relative; z-index: 1;">
              <div class="bt-progress-animate" style="
                background: ${isFirst ? 'white' : (labelColors[score.label] || '#999')};
                height: 100%;
                width: 0%;
                transition: width 1.2s cubic-bezier(0.4, 0, 0.2, 1), background 0.4s ease;
                border-radius: ${isFirst ? '12px' : '8px'};
                box-shadow: ${isFirst ? '0 2px 8px rgba(255,255,255,0.3)' : 'none'};
              " data-width="${Math.max(0, Math.min(100, score.score || 0))}"></div>
            </div>
            
            <div style="display: flex; justify-content: space-between; font-size: 12px; color: ${isFirst ? 'white' : '#666'}; font-weight: ${isFirst ? '600' : '500'}; position: relative; z-index: 1; transition: color 0.4s ease;">
              <span style="text-shadow: ${isFirst ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'};">Score: ${Math.round(score.score)}%</span>
              <span style="opacity: ${isFirst ? '0.9' : '1'}; text-shadow: ${isFirst ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'};">${score.questions_answered} questions</span>
            </div>
          </div>
          
          <!-- Collapsible Details Section -->
          <div 
            id="bt-accordion-content-${index}"
            class="bt-accordion-content ${isFirst ? 'expanded' : ''}"
            style="
              max-height: ${isFirst ? '2000px' : '0'};
              overflow: hidden;
              transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease;
              opacity: ${isFirst ? '1' : '0'};
            "
          >
            <div style="
              background: ${gradients[score.label] || gradients['on_track']};
              border-radius: 0 0 24px 24px;
              padding: 0 24px 24px 24px;
              margin-top: 0;
              position: relative;
              overflow: hidden;
            ">
              <!-- Decorative circles for content area -->
              <div style="position: absolute; top: 20px; right: -40px; width: 100px; height: 100px; background: rgba(255,255,255,0.08); border-radius: 50%; pointer-events: none;"></div>
              
              ${domainDetailsHTML}
              
              ${examples.length > 0 ? `
                <button class="bt-view-responses-btn" data-target="bt-examples-${index}" style="
                  background: rgba(255,255,255,0.2);
                  backdrop-filter: blur(10px);
                  border: 2px solid rgba(255,255,255,0.3);
                  color: white;
                  padding: 10px 20px;
                  border-radius: 16px;
                  font-size: 13px;
                  font-weight: 700;
                  cursor: pointer;
                  width: 100%;
                  margin-top: 16px;
                  transition: all 0.3s ease;
                  text-shadow: 0 1px 2px rgba(0,0,0,0.1);
                ">
                  View Your Responses ▼
                </button>
              ` : ''}
              
              ${examplesHTML}
              
              ${score.flags && score.flags.length > 0 ? `
                <div style="
                  background: rgba(255,255,255,0.2);
                  backdrop-filter: blur(10px);
                  color: white;
                  padding: 12px 16px;
                  border-radius: 16px;
                  margin-top: 16px;
                  font-size: 12px;
                  border: 1px solid rgba(255,255,255,0.3);
                  font-weight: 600;
                ">
                  ⚠️ ${score.flags.join(', ')}
                </div>
              ` : ''}
            </div>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div style="background: white; border-radius: 12px; padding: 24px; box-shadow: 0 2px 12px rgba(0,0,0,0.08);">
        <!-- Overview Header -->
        <div style="text-align: center; margin-bottom: 32px;">
          <h3 style="margin: 0 0 12px 0; font-size: 24px; font-weight: 700; color: var(--bt-charcoal); font-family: 'Quicksand', sans-serif;">📊 Development Overview</h3>
          <p style="margin: 0; font-size: 15px; color: #555; line-height: 1.6; max-width: 600px; margin: 0 auto;">${overallStatus}</p>
          <p style="margin: 12px 0 0 0; font-size: 13px; color: #7AA885; font-weight: 600;">👆 Click any domain to expand/collapse details</p>
        </div>
        
        <!-- Accordion Items -->
        ${accordionHTML}
      </div>
    `;
    
    // Trigger progress bar animations after DOM settles
    setTimeout(() => {
      document.querySelectorAll('.bt-progress-animate').forEach(bar => {
        const width = bar.getAttribute('data-width');
        if (width !== null && width !== undefined) {
          const numWidth = parseFloat(width);
          if (!isNaN(numWidth)) {
            bar.style.width = `${Math.max(0, Math.min(100, numWidth))}%`;
          }
        }
      });
    }, 100);
  }

  displayRecommendationBridge(data) {
    const container = document.getElementById('bt-recommendation-bridge');
    if (!container) return;

    const weakDomains = data.domain_scores
      .filter(s => s.label === 'practice_more' || s.label === 'watch_closely')
      .map(s => s.domain);

    let bridgeText = '';
    if (weakDomains.length > 0) {
      bridgeText = `Based on ${data.child_name}'s assessment, we noticed that ${weakDomains.slice(0, 2).join(' and ')} 
        ${weakDomains.length === 1 ? 'is an area' : 'are areas'} that would benefit from focused practice. 
        We've carefully selected toys that specifically target these developmental areas while being age-appropriate and engaging.`;
    } else {
      bridgeText = `${data.child_name} is developing wonderfully! We've selected toys that will provide enrichment 
        and support continued growth across all developmental domains.`;
    }

    container.innerHTML = `
      <div class="bt-bridge-content fade-in">
        <div class="bt-bridge-icon">🎯</div>
        <h3>Why These Recommendations?</h3>
        <p>${bridgeText}</p>
        <div class="bt-selection-process">
          <div class="bt-process-step">
            <span class="bt-step-number">1</span>
            <span class="bt-step-text">Analyze assessment results</span>
          </div>
          <div class="bt-process-arrow">→</div>
          <div class="bt-process-step">
            <span class="bt-step-number">2</span>
            <span class="bt-step-text">Match age & skills</span>
          </div>
          <div class="bt-process-arrow">→</div>
          <div class="bt-process-step">
            <span class="bt-step-number">3</span>
            <span class="bt-step-text">Personalized selection</span>
          </div>
        </div>
      </div>
    `;
  }

  displayRecommendations(recommendations, summary, childName) {
    const container = document.getElementById('bt-products-container');
    if (!container) return;

    // Render placeholder cards immediately with rationale text from API
    const placeholdersHTML = recommendations
      .map((rec, index) => {
        return `
          <div class="bt-product-card bt-assessment-product-card bt-product-loading fade-in" 
               data-product-index="${index}"
               data-product-handle="${rec.shopify_handle}"
               style="animation-delay: ${index * 0.1}s; background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1); transition: transform 0.3s, box-shadow 0.3s;">
            <!-- Skeleton Image Loader -->
            <div class="bt-product-image-wrapper bt-skeleton-image" style="position: relative; overflow: hidden; height: 220px; background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite;">
              <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 48px; opacity: 0.3;">🎁</div>
            </div>
            <div class="bt-product-content" style="padding: 20px;">
              <!-- Skeleton Title -->
              <div class="bt-skeleton-title" style="height: 20px; background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 4px; margin-bottom: 12px; width: 80%;"></div>
              
              <!-- Show rationale immediately (from API) -->
              <p class="bt-product-rationale" style="margin: 0 0 12px 0; font-size: 13px; color: #666; line-height: 1.6;">${rec.rationale || 'Loading product details...'}</p>
              
              <!-- Skeleton Price -->
              <div class="bt-skeleton-price" style="height: 24px; background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 4px; margin-bottom: 12px; width: 40%;"></div>
              
              <!-- Show skills if available from API -->
              ${rec.skills_addressed && rec.skills_addressed.length > 0 ? `
                <div class="bt-product-skills" style="margin-bottom: 16px; display: flex; flex-wrap: wrap; gap: 6px;">
                  ${rec.skills_addressed.slice(0, 3).map(skill => `
                    <span class="bt-skill-tag" style="display: inline-block; background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%); color: #555; padding: 5px 12px; border-radius: 14px; font-size: 11px; font-weight: 600;">${skill}</span>
                  `).join('')}
                </div>
              ` : ''}
              
              <!-- Skeleton Buttons -->
              <div class="bt-product-actions" style="display: flex; flex-direction: column; gap: 10px; margin-top: 16px;">
                <div class="bt-skeleton-button" style="height: 44px; background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 8px;"></div>
                <div class="bt-skeleton-button" style="height: 44px; background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%); background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 8px;"></div>
              </div>
            </div>
          </div>
        `;
      })
      .join('');

    container.innerHTML = `
      <div style="background: white; border-radius: 12px; padding: 24px; box-shadow: 0 2px 12px rgba(0,0,0,0.08);">
        <p style="color: #666; font-size: 15px; margin-bottom: 24px; line-height: 1.6;">${summary}</p>
        <div class="bt-products-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 20px;">
          ${placeholdersHTML}
        </div>
      </div>
    `;

    // Start loading products in background sequentially
    this.loadProductsInBackground(recommendations);
  }

  async loadProductsInBackground(recommendations) {
    // Load products one at a time with small delay to avoid overwhelming browser
    for (let i = 0; i < recommendations.length; i++) {
      const rec = recommendations[i];
      
      // Add delay between fetches to avoid rate limits
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      try {
        const productData = await this.fetchShopifyProduct(rec.shopify_handle, rec);
        this.updateProductCard(i, productData);
      } catch (error) {
        console.error(`Failed to load product ${rec.shopify_handle}:`, error);
        this.updateProductCardWithError(i, rec);
      }
    }
  }

  updateProductCard(index, productData) {
    const { product, rationale, skills } = productData;
    const card = document.querySelector(`[data-product-index="${index}"]`);
    
    if (!card) return;

    // Get variant ID for add to cart
    const variantId = product.variants && product.variants.length > 0 
      ? product.variants[0].id 
      : null;
    
    // Format price
    let priceHTML = '';
    if (product.priceRange && product.priceRange.minVariantPrice) {
      const price = parseFloat(product.priceRange.minVariantPrice.amount);
      const formattedPrice = new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0
      }).format(price);
      priceHTML = `<div class="bt-product-price" style="font-size: 18px; font-weight: 700; color: var(--bt-sage-green); margin-bottom: 12px;">${formattedPrice}</div>`;
    }

    // Build full product card HTML
    card.innerHTML = `
      <div class="bt-product-image-wrapper" style="position: relative; overflow: hidden;">
        ${product.images && product.images[0] ? `
          <a href="/products/${product.handle}" style="display: block;">
            <img 
              src="${product.images[0].src}" 
              alt="${product.title.replace(/"/g, '&quot;')}"
              class="bt-product-image-img"
              style="width: 100%; height: 220px; object-fit: cover; transition: transform 0.3s;"
              onmouseover="this.style.transform='scale(1.05)'"
              onmouseout="this.style.transform='scale(1)'"
            />
          </a>
        ` : `
          <div class="bt-product-image-placeholder" style="width: 100%; height: 220px; background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%); display: flex; align-items: center; justify-content: center;">
            <span style="font-size: 64px; opacity: 0.5;">🎁</span>
          </div>
        `}
      </div>
      <div class="bt-product-content" style="padding: 20px;">
        <h5 class="bt-product-title" style="margin: 0 0 12px 0; font-size: 17px; font-weight: 700; color: var(--bt-charcoal); line-height: 1.3;">
          <a href="/products/${product.handle}" style="color: inherit; text-decoration: none;">
            ${product.title}
          </a>
        </h5>
        <p class="bt-product-rationale" style="margin: 0 0 12px 0; font-size: 13px; color: #666; line-height: 1.6;">${rationale}</p>
        ${priceHTML}
        ${skills && skills.length > 0 ? `
          <div class="bt-product-skills" style="margin-bottom: 16px; display: flex; flex-wrap: wrap; gap: 6px;">
            ${skills.slice(0, 3).map(skill => `
              <span class="bt-skill-tag" style="display: inline-block; background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%); color: #555; padding: 5px 12px; border-radius: 14px; font-size: 11px; font-weight: 600;">${skill}</span>
            `).join('')}
          </div>
        ` : ''}
        <div class="bt-product-actions" style="display: flex; flex-direction: column; gap: 10px; margin-top: 16px;">
          ${variantId ? `
            <button 
              class="bt-add-to-cart-btn" 
              data-variant-id="${variantId}"
              data-product-title="${product.title.replace(/"/g, '&quot;')}"
              style="
                width: 100%;
                background: var(--bt-sage-green);
                color: white;
                border: none;
                padding: 12px 20px;
                border-radius: 8px;
                font-weight: 600;
                font-size: 14px;
                cursor: pointer;
                transition: all 0.3s;
                box-shadow: 0 2px 6px rgba(122, 168, 133, 0.3);
              "
              onmouseover="this.style.background='#6a9a75'; this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 12px rgba(122, 168, 133, 0.4)'"
              onmouseout="this.style.background='var(--bt-sage-green)'; this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 6px rgba(122, 168, 133, 0.3)'"
            >
              Add to Cart
            </button>
          ` : ''}
          <a 
            href="/products/${product.handle}" 
            style="
              width: 100%;
              display: block;
              background: transparent;
              color: var(--bt-sage-green);
              border: 2px solid var(--bt-sage-green);
              padding: 10px 20px;
              border-radius: 8px;
              text-align: center;
              font-weight: 600;
              font-size: 14px;
              text-decoration: none;
              transition: all 0.3s;
              box-sizing: border-box;
            "
            onmouseover="this.style.background='var(--bt-sage-green)'; this.style.color='white'"
            onmouseout="this.style.background='transparent'; this.style.color='var(--bt-sage-green)'"
          >
            View Details →
          </a>
        </div>
      </div>
    `;

    // Remove loading class
    card.classList.remove('bt-product-loading');
    
    // Update variant ID attribute
    card.setAttribute('data-variant-id', variantId || '');
    
    // Rebind add to cart for this card
    this.bindAddToCartButtons();
  }

  updateProductCardWithError(index, recommendation) {
    const card = document.querySelector(`[data-product-index="${index}"]`);
    
    if (!card) return;

    // Extract clean handle for fallback link
    let cleanHandle = recommendation.shopify_handle;
    if (cleanHandle.includes('://')) {
      cleanHandle = cleanHandle.split('/products/').pop();
    }
    cleanHandle = cleanHandle.replace(/^\/+|\/+$/g, '');

    card.innerHTML = `
      <div class="bt-product-image-wrapper" style="position: relative; overflow: hidden;">
        <div class="bt-product-image-placeholder" style="width: 100%; height: 220px; background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%); display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 8px;">
          <span style="font-size: 48px; opacity: 0.5;">📦</span>
          <span style="font-size: 12px; color: #999;">Product unavailable</span>
        </div>
      </div>
      <div class="bt-product-content" style="padding: 20px;">
        <h5 class="bt-product-title" style="margin: 0 0 12px 0; font-size: 17px; font-weight: 700; color: var(--bt-charcoal); line-height: 1.3;">
          ${recommendation.title || 'Recommended Product'}
        </h5>
        <p class="bt-product-rationale" style="margin: 0 0 12px 0; font-size: 13px; color: #666; line-height: 1.6;">${recommendation.rationale || 'This product is currently unavailable.'}</p>
        ${recommendation.skills_addressed && recommendation.skills_addressed.length > 0 ? `
          <div class="bt-product-skills" style="margin-bottom: 16px; display: flex; flex-wrap: wrap; gap: 6px;">
            ${recommendation.skills_addressed.slice(0, 3).map(skill => `
              <span class="bt-skill-tag" style="display: inline-block; background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%); color: #555; padding: 5px 12px; border-radius: 14px; font-size: 11px; font-weight: 600;">${skill}</span>
            `).join('')}
          </div>
        ` : ''}
        <div class="bt-product-actions" style="display: flex; flex-direction: column; gap: 10px; margin-top: 16px;">
          <a 
            href="/products/${cleanHandle}" 
            style="
              width: 100%;
              display: block;
              background: var(--bt-sage-green);
              color: white;
              padding: 12px 20px;
              border-radius: 8px;
              text-align: center;
              font-weight: 600;
              font-size: 14px;
              text-decoration: none;
              transition: all 0.3s;
              box-sizing: border-box;
            "
            onmouseover="this.style.background='#6a9a75'"
            onmouseout="this.style.background='var(--bt-sage-green)'"
          >
            View on Store →
          </a>
        </div>
      </div>
    `;

    // Remove loading class
    card.classList.remove('bt-product-loading');
  }

  async fetchShopifyProduct(handleRaw, recommendation) {
    try {
      // Clean the handle - remove any URL prefix if present
      let handle = handleRaw;
      if (handle.includes('://')) {
        // Handle is a full URL like "https://brainytots.com/products/baby-toy"
        // Extract just the handle part
        handle = handle.split('/products/').pop();
      }
      // Remove any leading/trailing slashes
      handle = handle.replace(/^\/+|\/+$/g, '');
      
      console.log('Fetching product with handle:', handle);
      
      // Use Shopify AJAX API instead of Storefront GraphQL to avoid CORS issues
      // This is the recommended approach for theme-based product fetching
      const response = await fetch(`/products/${handle}.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Product API error: ${response.status}`);
      }
      
      const product = await response.json();
      
      if (!product || !product.id) {
        throw new Error(`Product not found: ${handle}`);
      }

      // Transform Shopify AJAX API format to match our expected format
      // AJAX API returns a different structure than Storefront API
      const transformedProduct = {
        id: product.id,
        title: product.title,
        handle: product.handle,
        description: product.description,
        images: product.images ? product.images.map(img => ({
          src: img,
          altText: product.title
        })) : [],
        priceRange: {
          minVariantPrice: {
            amount: product.price ? (product.price / 100).toString() : '0',
            currencyCode: 'INR'
          }
        },
        variants: product.variants ? product.variants.map(v => ({
          id: v.id.toString(),
          title: v.title || product.title,
          price: {
            amount: v.price ? (v.price / 100).toString() : '0'
          },
          availableForSale: v.available
        })) : [],
        availableForSale: product.available
      };

      return {
        product: transformedProduct,
        rationale: recommendation.rationale,
        skills: recommendation.skills_addressed
      };
    } catch (error) {
      console.error(`Error fetching product ${handleRaw}:`, error);
      
      // Extract clean handle for fallback
      let cleanHandle = handleRaw;
      if (handleRaw.includes('://')) {
        cleanHandle = handleRaw.split('/products/').pop();
      }
      cleanHandle = cleanHandle.replace(/^\/+|\/+$/g, '');
      
      // Return a placeholder
      return {
        product: {
          id: 'placeholder',
          title: recommendation.title || 'Product',
          handle: cleanHandle,
          images: [],
          priceRange: {
            minVariantPrice: {
              amount: '0',
              currencyCode: 'INR'
            }
          },
          variants: [],
          availableForSale: false
        },
        rationale: recommendation.rationale || 'This product is currently unavailable.',
        skills: recommendation.skills_addressed || []
      };
    }
  }

  bindAddToCartButtons() {
    document.querySelectorAll('.bt-add-to-cart-btn').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = btn.getAttribute('data-variant-id');
        const productTitle = btn.getAttribute('data-product-title');
        
        if (!variantId) {
          console.error('No variant ID found');
          return;
        }
        
        // Convert Storefront API ID to numeric ID for cart.js
        // Storefront IDs look like: "gid://shopify/ProductVariant/12345"
        const numericId = variantId.includes('gid://') 
          ? variantId.split('/').pop() 
          : variantId;
        
        await this.addToShopifyCart(numericId, productTitle, btn);
      });
    });
  }

  async addToShopifyCart(variantId, productTitle, button) {
    const originalText = button.textContent;
    const originalBg = button.style.background;
    
    try {
      // Update button state
      button.disabled = true;
      button.textContent = 'Adding...';
      button.style.background = '#95a5a6';
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1
          }]
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Added to cart:', data);
        
        // Success state
        button.textContent = '✓ Added to Cart!';
        button.style.background = '#27AE60';
        
        // Dispatch cart update event for theme's cart drawer
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          cancelable: true
        }));
        
        // Show success message (if theme has notifications)
        if (window.theme && window.theme.showQuickAdd) {
          // Some themes have this
        } else {
          // Fallback: show simple notification
          this.showNotification(`${productTitle} added to cart!`, 'success');
        }
        
        // Reset button after 3 seconds
        setTimeout(() => {
          button.disabled = false;
          button.textContent = originalText;
          button.style.background = originalBg;
        }, 3000);
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Cart API error:', errorData);
        throw new Error(errorData.description || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      button.textContent = '✗ Error - Try Again';
      button.style.background = '#E74C3C';
      this.showNotification(`Could not add ${productTitle} to cart. Please try again.`, 'error');
      
      // Reset button after 3 seconds
      setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
        button.style.background = originalBg;
      }, 3000);
    }
  }

  showNotification(message, type = 'info') {
    // Create a simple toast notification
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      bottom: 30px;
      right: 30px;
      background: ${type === 'success' ? '#27AE60' : type === 'error' ? '#E74C3C' : '#3498DB'};
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 600;
      max-width: 350px;
      animation: slideInUp 0.3s ease;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    // Add animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInUp {
        from {
          transform: translateY(100px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Remove after 4 seconds
    setTimeout(() => {
      toast.style.animation = 'slideInUp 0.3s ease reverse';
      setTimeout(() => {
        document.body.removeChild(toast);
        document.head.removeChild(style);
      }, 300);
    }, 4000);
  }

  displayActivities(activities) {
    const container = document.getElementById('bt-activities-container');
    if (!container) return;

    const domainColors = {
      'Gross Motor': '#27AE60',
      'Fine Motor': '#3498DB',
      'Language & Communication': '#9B59B6',
      'Social-Emotional': '#E67E22',
      'Cognitive/Problem-Solving': '#E74C3C',
      'General': '#95A5A6',
    };

    const activitiesHTML = activities
      .map((activity, index) => `
        <div class="bt-activity-card fade-in" style="animation-delay: ${index * 0.1}s; border-left: 4px solid ${domainColors[activity.domain] || '#999'};">
          <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
            <h5 style="margin: 0; color: var(--bt-charcoal); font-size: 16px;">${activity.title}</h5>
            <span style="
              background: ${domainColors[activity.domain] || '#999'};
              color: white;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 600;
            ">${activity.domain}</span>
          </div>
          <p style="margin: 0 0 8px 0; color: #666; font-size: 14px; line-height: 1.5;">
            ${activity.description}
          </p>
          ${activity.materials_needed && activity.materials_needed.length > 0 ? `
            <div style="font-size: 12px; color: #999; margin-top: 8px;">
              📦 Materials: ${activity.materials_needed.join(', ')}
            </div>
          ` : ''}
        </div>
      `)
      .join('');

    container.innerHTML = `
      <div style="background: white; border-radius: 12px; padding: 24px; box-shadow: 0 2px 12px rgba(0,0,0,0.08);">
        <div style="display: grid; gap: 16px;">
          ${activitiesHTML}
        </div>
      </div>
    `;
  }

  generateProductUrl(handle) {
    // Handle already includes the full URL or is just the handle
    if (handle && handle.startsWith('http')) {
      return handle;
    }
    // Clean the handle and create Shopify URL
    const cleanHandle = handle.replace(/^\/products\//, '').replace(/^products\//, '');
    return `https://brainytots.com/products/${cleanHandle}`;
  }

  bindEvents() {
    // Methodology toggle
    const methodologyToggle = document.getElementById('bt-methodology-toggle');
    const methodologyContent = document.getElementById('bt-methodology-content');
    
    if (methodologyToggle && methodologyContent) {
      methodologyToggle.addEventListener('click', () => {
        const isVisible = methodologyContent.style.display !== 'none';
        methodologyContent.style.display = isVisible ? 'none' : 'block';
        const icon = methodologyToggle.querySelector('.bt-toggle-icon');
        if (icon) {
          icon.textContent = isVisible ? '+' : '−';
        }
      });
    }

    // View details buttons for domain scores (old responses toggle - keep this)
    document.querySelectorAll('.bt-view-details-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const targetId = e.currentTarget.getAttribute('data-target');
        const targetEl = document.getElementById(targetId);
        if (targetEl) {
          const isVisible = targetEl.style.display !== 'none';
          targetEl.style.display = isVisible ? 'none' : 'block';
          e.currentTarget.innerHTML = isVisible ? 'View Your Responses ▼' : 'Hide Your Responses ▲';
        }
      });
    });
    
    // Accordion header clicks - expand/collapse inline with gradient transition
    document.querySelectorAll('.bt-accordion-header').forEach(header => {
      header.addEventListener('click', (e) => {
        const accordionItem = header.closest('.bt-accordion-item');
        const contentId = header.getAttribute('aria-controls');
        const content = document.getElementById(contentId);
        const icon = header.querySelector('.bt-accordion-icon');
        const hintText = header.querySelector('div[style*="Tap to"]');
        const emoji = header.querySelector('.bt-domain-emoji');
        const domainTitle = header.querySelector('h4');
        const statusBadge = header.querySelector('span[style*="backdrop-filter"], span[style*="background:"]');
        const scoreTexts = header.querySelectorAll('div[style*="justify-content: space-between"] span');
        const progressTrack = header.querySelector('div[style*="background:"][style*="border-radius"]');
        const progressBar = header.querySelector('.bt-progress-animate');
        
        // Get gradient and color data
        const gradient = header.getAttribute('data-gradient');
        const shadow = header.getAttribute('data-shadow');
        const borderColor = header.getAttribute('data-border-color');
        
        if (content && icon) {
          const isExpanded = content.classList.contains('expanded');
          
          if (isExpanded) {
            // Collapse: Transition back to white card
            content.classList.remove('expanded');
            content.style.maxHeight = '0';
            content.style.opacity = '0';
            header.classList.remove('expanded');
            
            // Animate back to closed state
            setTimeout(() => {
              header.style.background = 'white';
              header.style.border = `2px solid ${borderColor}`;
              header.style.borderRadius = '16px';
              header.style.padding = '16px 20px';
              header.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
              header.style.overflow = 'visible';
              
              if (emoji) {
                emoji.style.fontSize = '32px';
                emoji.style.filter = 'none';
              }
              
              if (domainTitle) {
                domainTitle.style.color = 'var(--bt-charcoal)';
                domainTitle.style.fontWeight = '600';
                domainTitle.style.textShadow = 'none';
              }
              
              if (statusBadge) {
                statusBadge.style.background = borderColor;
                statusBadge.style.backdropFilter = 'none';
                statusBadge.style.border = 'none';
                statusBadge.style.textShadow = 'none';
              }
              
              if (progressTrack) {
                progressTrack.style.background = '#f5f5f5';
                progressTrack.style.borderRadius = '8px';
                progressTrack.style.height = '8px';
              }
              
              if (progressBar) {
                progressBar.style.background = borderColor;
                progressBar.style.borderRadius = '8px';
                progressBar.style.boxShadow = 'none';
              }
              
              scoreTexts.forEach(span => {
                span.style.color = '#666';
                span.style.fontWeight = '500';
                span.style.textShadow = 'none';
              });
              
              if (icon) {
                icon.style.color = borderColor;
                icon.style.filter = 'none';
              }
              
              // Remove decorative circles
              const circles = header.querySelectorAll('div[style*="border-radius: 50%"]');
              circles.forEach(circle => circle.remove());
              
            }, 50);
            
            icon.style.transform = 'rotate(0deg)';
            header.setAttribute('aria-expanded', 'false');
            if (hintText) hintText.textContent = 'Tap to expand';
            
          } else {
            // Expand: Transition to gradient
            content.classList.add('expanded');
            content.style.maxHeight = '2000px';
            content.style.opacity = '1';
            header.classList.add('expanded');
            
            // Animate to open state
            header.style.background = gradient;
            header.style.border = '2px solid transparent';
            header.style.borderRadius = '24px 24px 0 0';
            header.style.padding = '20px 24px';
            header.style.boxShadow = `0 8px 24px ${shadow}`;
            header.style.overflow = 'hidden';
            
            if (emoji) {
              emoji.style.fontSize = '40px';
              emoji.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))';
            }
            
            if (domainTitle) {
              domainTitle.style.color = 'white';
              domainTitle.style.fontWeight = '700';
              domainTitle.style.textShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }
            
            if (statusBadge) {
              statusBadge.style.background = 'rgba(255,255,255,0.25)';
              statusBadge.style.backdropFilter = 'blur(10px)';
              statusBadge.style.border = '1px solid rgba(255,255,255,0.2)';
              statusBadge.style.textShadow = '0 1px 2px rgba(0,0,0,0.1)';
            }
            
            if (progressTrack) {
              progressTrack.style.background = 'rgba(255,255,255,0.25)';
              progressTrack.style.borderRadius = '12px';
              progressTrack.style.height = '10px';
            }
            
            if (progressBar) {
              progressBar.style.background = 'white';
              progressBar.style.borderRadius = '12px';
              progressBar.style.boxShadow = '0 2px 8px rgba(255,255,255,0.3)';
            }
            
            scoreTexts.forEach((span, idx) => {
              span.style.color = 'white';
              span.style.fontWeight = '600';
              span.style.textShadow = '0 1px 2px rgba(0,0,0,0.1)';
              if (idx === 1) span.style.opacity = '0.9';
            });
            
            if (icon) {
              icon.style.color = 'white';
              icon.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))';
            }
            
            // Add decorative circles
            if (!header.querySelector('div[style*="border-radius: 50%"]')) {
              const circle1 = document.createElement('div');
              circle1.style.cssText = 'position: absolute; top: -30px; right: -30px; width: 120px; height: 120px; background: rgba(255,255,255,0.1); border-radius: 50%; pointer-events: none; z-index: 0;';
              const circle2 = document.createElement('div');
              circle2.style.cssText = 'position: absolute; bottom: -20px; left: -20px; width: 80px; height: 80px; background: rgba(255,255,255,0.1); border-radius: 50%; pointer-events: none; z-index: 0;';
              header.insertBefore(circle1, header.firstChild);
              header.insertBefore(circle2, header.firstChild);
            }
            
            icon.style.transform = 'rotate(180deg)';
            header.setAttribute('aria-expanded', 'true');
            if (hintText) hintText.textContent = 'Tap to collapse';
          }
        }
      });
      
      // Keyboard accessibility
      header.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          header.click();
        }
      });
    });
    
    // View responses buttons (inside accordion content)
    document.querySelectorAll('.bt-view-responses-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent accordion toggle
        const targetId = btn.getAttribute('data-target');
        const targetEl = document.getElementById(targetId);
        if (targetEl) {
          const isVisible = targetEl.style.display !== 'none';
          targetEl.style.display = isVisible ? 'none' : 'block';
          btn.innerHTML = isVisible ? 'View Your Responses ▼' : 'Hide Your Responses ▲';
        }
      });
    });

    // Download and WhatsApp buttons
    const downloadBtn = document.getElementById('bt-download-pdf');
    const whatsappBtn = document.getElementById('bt-send-whatsapp');

    if (downloadBtn) {
      downloadBtn.addEventListener('click', () => this.handleDownloadPDF());
    }

    if (whatsappBtn) {
      whatsappBtn.addEventListener('click', () => this.handleSendWhatsApp());
    }
  }

  triggerScrollAnimations() {
    // Add intersection observer for fade-in animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.fade-in').forEach(el => {
      observer.observe(el);
    });
  }

  handleDownloadPDF() {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      console.log('Downloading PDF for session:', sessionId);
      alert('PDF download feature coming soon! Your results are saved in your account.');
    }
  }

  handleSendWhatsApp() {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      console.log('Sending results to WhatsApp for session:', sessionId);
      const shareText = `Check out ${this.childName}'s personalized developmental assessment results!`;
      const shareUrl = window.location.href;
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
      window.open(whatsappUrl, '_blank');
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  new BrainyTotsResults();
});
